// Helpers de representantes/sócios do cedente (item 22) — ESPELHOS client-side
// das validações da V2, para feedback imediato no form. NÃO substituem a V2:
// a regra de negócio (dedup 409, documento travado, anti-injeção, completude
// p/ assinar) vive lá e o erro da V2 é sempre exibido com fidelidade.

// Cap de assinantes por contrato — espelho de MAX_SIGNERS da V2
// (~/zemo/v2/backend/app/domain/contract_builder.py: template PJ tem
// QUALIFICACAO-SOCIO-1..7).
export const MAX_SIGNERS = 7;

export const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

// PJ = CNPJ (14 dígitos); PF = CPF (11). Predicado ÚNICO do fluxo de
// assinantes (PJ assina via representantes; PF assina ele mesmo) — usado no
// wizard (NewOperation) e no detalhe do cedente (Clients).
export const isPJDocument = (doc) => onlyDigits(doc).length === 14;

export function maskCPF(v) {
  return onlyDigits(v)
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

export function maskCEP(v) {
  return onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, "$1-$2");
}

// ── Telefone (feedback do owner 2026-07-11) ────────────────────────────────
// ARMAZENAMENTO na V2: campo ÚNICO livre (phone VARCHAR(25), sem split de
// DDI — alembic 0044) que flui VERBATIM para o texto do contrato
// (contract_builder.py:205 "Telefone {phone}"); a ZapSign não recebe phone
// estruturado. O payload vai FORMATADO como exibido (WYSIWYG): BR
// "(11) 99999-9999"; outro país "+DDI dígitos". Anti-injeção da V2 ({} e
// controle) não afeta parênteses/hífen/+.

// Remove o DDI 55 EMBUTIDO — só quando os dígitos são DDI+DDD+número
// (12/13 díg. começando com 55: colagem de "+55 11 99999-9999" ou legado
// armazenado). 10/11 dígitos NUNCA são stripados: DDD 55 (RS) é legítimo
// ("55 3222-1234" fixo / "55 99999-9999" celular). Fonte ÚNICA da heurística
// (usada pela máscara e pelo parseStoredPhone).
export function stripBRCountryCode(digits) {
  const d = onlyDigits(digits);
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) return d.slice(2);
  return d;
}

// Máscara dinâmica BR: (xx) xxxx-xxxx (fixo, 10 díg.) e o 9º dígito desloca
// o hífen — (xx) xxxxx-xxxx (celular, 11 díg.). Usuário digita SÓ DDD+número;
// colar "+55 11 99999-9999" NÃO vira DDD falso (strip do DDI embutido ANTES
// da máscara — achado da escada 2026-07-11).
export function maskPhoneBR(v) {
  const d = stripBRCountryCode(v).slice(0, 11);
  if (!d) return "";
  if (d.length <= 2) return `(${d}`;
  const rest = d.slice(2);
  if (rest.length <= 4) return `(${d.slice(0, 2)}) ${rest}`;
  const split = rest.length > 8 ? 5 : 4;
  return `(${d.slice(0, 2)}) ${rest.slice(0, split)}-${rest.slice(split)}`;
}

// Telefone BR válido = DDD + número (10 ou 11 dígitos), DDD não começa em 0.
export function isValidPhoneBR(v) {
  return /^[1-9]\d{9,10}$/.test(onlyDigits(v));
}

// Monta o valor persistido (campo único da V2) a partir do estado do form.
export function composePhonePayload({ country, phone, phone_ddi }) {
  if (String(country || "BR").trim().toUpperCase() === "BR") return maskPhoneBR(phone);
  const ddi = onlyDigits(phone_ddi);
  const number = onlyDigits(phone);
  return ddi && number ? `+${ddi} ${number}` : "";
}

// Desmonta o valor armazenado para edição. País vem do cadastro (default BR).
// BR legado "+55 11 99999-9999" (12/13 dígitos com DDI embutido) → exibe só
// DDD+número; não-BR "+DDI resto" → separa DDI; sem "+" → só dígitos.
export function parseStoredPhone(stored, country) {
  const c = String(country || "BR").trim().toUpperCase() || "BR";
  const raw = String(stored || "").trim();
  if (c === "BR") {
    // strip do DDI embutido é o do maskPhoneBR (stripBRCountryCode) — fonte única.
    return { country: "BR", phone_ddi: "", phone: maskPhoneBR(raw) };
  }
  const m = raw.match(/^\+(\d{1,3})[\s.-]*(.*)$/);
  if (m) return { country: c, phone_ddi: m[1], phone: onlyDigits(m[2]) };
  return { country: c, phone_ddi: "", phone: onlyDigits(raw) };
}

// DV mod-11 do CPF — espelho do validate_document da V2 (feedback imediato;
// a V2 revalida fail-closed no create).
export function isValidCPF(cpf) {
  const d = onlyDigits(cpf);
  if (d.length !== 11 || new Set(d).size === 1) return false;
  const dv = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += parseInt(d[i], 10) * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return dv(9) === parseInt(d[9], 10) && dv(10) === parseInt(d[10], 10);
}

// Regex mínima de e-mail — MESMO critério da V2 (_EMAIL_RE em
// representatives.py: algo@algo.tld; o e-mail é o canal de assinatura ZapSign).
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function isValidEmail(v) {
  return EMAIL_RE.test(String(v || "").trim());
}

// Espelho do reject_template_injection da V2 (contract_builder): os dados do
// representante fluem VERBATIM para o texto do contrato — chaves {} e
// caracteres de controle são rejeitados (acentos/apóstrofos passam).
// eslint-disable-next-line no-control-regex
const FORBIDDEN_TEMPLATE_RE = /[{}\u0000-\u001f\u007f]/;

export function hasForbiddenTemplateChars(v) {
  return FORBIDDEN_TEMPLATE_RE.test(String(v || ""));
}

// Espelho de SIGNER_REQUIRED_FIELDS da V2 (contract_builder.py:89): campos
// exigidos para ASSINAR (a qualificação jurídica do contrato usa telefone e
// endereço). O cadastro aceita parcial; o gate é na seleção como assinante.
export const SIGNER_REQUIRED_FIELDS = [
  ["phone", "telefone"],
  ["street", "rua"],
  ["city", "cidade"],
  ["state", "UF"],
  ["zip_code", "CEP"],
];

// Rótulos PT-BR dos campos que faltam p/ o representante poder assinar.
export function signerMissingFields(rep) {
  return SIGNER_REQUIRED_FIELDS
    .filter(([key]) => !String(rep?.[key] || "").trim())
    .map(([, label]) => label);
}

export const isSignerComplete = (rep) => signerMissingFields(rep).length === 0;

// Validação client-side do form de representante (espelho, feedback imediato).
// Retorna { campo: mensagem }; vazio = sem erro local.
//
// Decisão de produto do owner (2026-07-11): TODOS os campos são obrigatórios
// EXCETO Complemento (país tem default BR). A V2 devolve 422 fail-closed para
// o MESMO conjunto — aqui é UX, lá é a autoridade.
export function validateRepresentativeForm(form, { documentEditable = true } = {}) {
  const errors = {};
  const name = String(form.full_name || "").trim();
  if (name.length < 2) errors.full_name = "Informe o nome completo (mínimo 2 caracteres).";
  else if (hasForbiddenTemplateChars(name)) {
    errors.full_name = "Nome não pode conter chaves {} nem caracteres de controle.";
  }
  if (documentEditable) {
    if (!isValidCPF(form.document)) errors.document = "CPF inválido — confira os dígitos.";
  }
  if (!isValidEmail(form.email)) errors.email = "E-mail inválido (ex.: nome@empresa.com).";
  else if (hasForbiddenTemplateChars(form.email)) {
    errors.email = "E-mail não pode conter chaves {} nem caracteres de controle.";
  }

  const country = String(form.country || "BR").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(country)) {
    errors.country = "País inválido — use o código de 2 letras (ex.: BR).";
  }
  // Telefone: BR = DDD+número mascarado; outro país = DDI (1-3 díg.) + número livre.
  if (country === "BR") {
    if (!isValidPhoneBR(form.phone)) {
      errors.phone = "Telefone inválido — informe DDD + número (10 ou 11 dígitos).";
    }
  } else {
    if (!/^\d{1,3}$/.test(onlyDigits(form.phone_ddi))) {
      errors.phone_ddi = "DDI inválido — 1 a 3 dígitos (ex.: 351).";
    }
    const number = onlyDigits(form.phone);
    if (number.length < 6 || number.length > 15) {
      errors.phone = "Telefone inválido — informe de 6 a 15 dígitos.";
    }
  }
  if (onlyDigits(form.zip_code).length !== 8) {
    errors.zip_code = "CEP inválido — informe os 8 dígitos.";
  }
  for (const [key, message] of [
    ["street", "Informe a rua."],
    ["street_number", "Informe o número."],
    ["neighborhood", "Informe o bairro."],
    ["city", "Informe a cidade."],
    ["state", "Selecione a UF."],
  ]) {
    if (!String(form[key] || "").trim()) errors[key] = message;
  }

  for (const [key, label] of [
    ["phone", "Telefone"], ["zip_code", "CEP"], ["street", "Rua"],
    ["street_number", "Número"], ["complement", "Complemento"],
    ["neighborhood", "Bairro"], ["city", "Cidade"], ["state", "UF"],
  ]) {
    if (!errors[key] && form[key] && hasForbiddenTemplateChars(form[key])) {
      errors[key] = `${label} não pode conter chaves {} nem caracteres de controle.`;
    }
  }
  return errors;
}
